<template>
  <div class="design-system">
    <div class="ds-hero mb-8">
      <div class="ds-hero__content">
        <div class="ds-hero__eyebrow">Référence interne</div>
        <h1 class="ds-hero__title">Charte Graphique</h1>
        <p class="ds-hero__copy">
          Catalogue vivant des fondations visuelles et des composants partagés
          utilisés dans l'application RMR.
        </p>
        <div class="ds-hero__tags">
          <button
            v-for="section in heroSections"
            :key="section.id"
            type="button"
            class="ds-hero__tag ds-hero__tag--link"
            @click="jumpToSection(section.id)"
          >
            {{ section.label }}
          </button>
        </div>
      </div>
      <div class="ds-hero__side">
        <dev-menu />
      </div>
    </div>

    <!-- ================================================================ -->
    <!-- SECTION: RmrActionButton                                         -->
    <!-- Variants: primary, ghost, transparent, translucent               -->
    <!-- Props: label, variant, size, icon, color, active, loading,      -->
    <!--        disabled                                                  -->
    <!-- ================================================================ -->
    <v-card id="ds-actions" class="ds-section mb-6">
      <v-card-title class="ds-section__head">
        <div class="ds-section__meta">
          <div class="ds-section__eyebrow">Actions</div>
          <div class="ds-section__title">RmrActionButton</div>
          <div class="ds-section__copy">
            Bouton principal du design system avec variantes, tailles et états.
          </div>
        </div>
      </v-card-title>
      <v-card-text>
        <!-- Standard variants - rendered from standardButtonExamples array in setup() -->
        <div class="ds-section-label mb-4">Standard Variants</div>
        <div class="ds-button-grid mb-6">
          <div
            v-for="button in standardButtonExamples"
            :key="button.label"
            class="ds-button-item"
          >
            <div class="ds-item-label">{{ button.label }}</div>
            <rmr-action-button
              :label="button.label"
              :variant="button.variant"
              :size="button.size || 'md'"
              :loading="button.loading || false"
              :disabled="button.disabled || false"
              :active="button.active || false"
              :color="button.color || null"
              :icon="button.icon || ''"
            />
          </div>
        </div>

        <div class="ds-section-label mb-4">Sizes</div>
        <div class="ds-button-grid mb-6">
          <div
            v-for="button in buttonSizeExamples"
            :key="button.label"
            class="ds-button-item"
          >
            <div class="ds-item-label">{{ button.label }}</div>
            <rmr-action-button
              :label="button.text"
              :variant="button.variant"
              :size="button.size"
              :icon="button.icon || ''"
            />
          </div>
        </div>

        <!-- Translucent variant - designed for dark/gradient backgrounds -->
        <div class="ds-section-label mb-4">Translucent On Dark Surface</div>
        <div class="ds-dark-bg">
          <div
            v-for="button in translucentButtonExamples"
            :key="button.label"
            class="ds-dark-bg__item"
          >
            <div class="ds-item-label ds-item-label--on-dark">
              {{ button.label }}
            </div>
            <rmr-action-button
              :label="button.label"
              :variant="button.variant"
              :size="button.size || 'md'"
              :loading="button.loading || false"
              :disabled="button.disabled || false"
              :active="button.active || false"
              :color="button.color || null"
              :icon="button.icon || ''"
            />
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- ================================================================ -->
    <!-- SECTION: RmrSegmentedControl                                     -->
    <!-- Variants: plain (light surfaces), on-dark (hero / gradients)     -->
    <!-- Props: value (v-model), items, variant, size, block              -->
    <!-- ================================================================ -->
    <v-card id="ds-segmented" class="ds-section mb-6">
      <v-card-title class="ds-section__head">
        <div class="ds-section__meta">
          <div class="ds-section__eyebrow">Actions</div>
          <div class="ds-section__title">RmrSegmentedControl</div>
          <div class="ds-section__copy">
            Bascule compacte entre quelques options exclusives : vues, périodes,
            tris.
          </div>
        </div>
      </v-card-title>
      <v-card-text>
        <div class="ds-section-label mb-4">Variante plain</div>
        <div class="ds-segmented-row mb-6">
          <div class="ds-segmented-item">
            <div class="ds-item-label">Tri</div>
            <rmr-segmented-control
              v-model="segmentedValues.sort"
              :items="segmentedSortItems"
            />
          </div>
          <div class="ds-segmented-item">
            <div class="ds-item-label">Vue (avec icônes)</div>
            <rmr-segmented-control
              v-model="segmentedValues.view"
              :items="segmentedViewItems"
            />
          </div>
          <div class="ds-segmented-item">
            <div class="ds-item-label">Taille md</div>
            <rmr-segmented-control
              v-model="segmentedValues.sort"
              :items="segmentedSortItems"
              size="md"
            />
          </div>
        </div>

        <div class="ds-section-label mb-4">Pleine largeur (block)</div>
        <div class="ds-segmented-block mb-6">
          <rmr-segmented-control
            v-model="segmentedValues.period"
            :items="segmentedPeriodItems"
            block
          />
        </div>

        <div class="ds-section-label mb-4">Variante on-dark</div>
        <div class="ds-dark-bg">
          <div class="ds-dark-bg__item">
            <div class="ds-item-label ds-item-label--on-dark">
              Sur fond dégradé
            </div>
            <rmr-segmented-control
              v-model="segmentedValues.tab"
              :items="segmentedTabItems"
              variant="on-dark"
            />
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- ================================================================ -->
    <!-- SECTION: RmrPagination                                           -->
    <!-- Props: value (v-model), length, total-visible, aria-label        -->
    <!-- ================================================================ -->
    <v-card id="ds-pagination" class="ds-section mb-6">
      <v-card-title class="ds-section__head">
        <div class="ds-section__meta">
          <div class="ds-section__eyebrow">Actions</div>
          <div class="ds-section__title">RmrPagination</div>
          <div class="ds-section__copy">
            Navigation paginée compacte sur fond clair, avec page active et
            contrôles clavier visibles.
          </div>
        </div>
      </v-card-title>
      <v-card-text>
        <rmr-pagination
          v-model="paginationPage"
          :length="8"
          :total-visible="7"
        />
      </v-card-text>
    </v-card>

    <!-- ================================================================ -->
    <!-- SECTION: Inputs & Controls                                       -->
    <!-- ================================================================ -->
    <v-card id="ds-forms" class="ds-section mb-6">
      <v-card-title class="ds-section__head">
        <div class="ds-section__meta">
          <div class="ds-section__eyebrow">Formulaires</div>
          <div class="ds-section__title">Inputs &amp; Controls</div>
          <div class="ds-section__copy">
            Champs, sélecteurs et contrôles de saisie avec les styles RMR
            partagés.
          </div>
        </div>
      </v-card-title>
      <v-card-text>
        <!-- RmrTextField - wrapper around v-text-field with RMR styles -->
        <div class="ds-section-label mb-4">RmrTextField</div>
        <div class="ds-form-grid mb-6">
          <rmr-text-field v-model="textFieldValues.empty" label="Champ vide" />
          <rmr-text-field
            v-model="textFieldValues.filled"
            label="Champ rempli"
          />
          <rmr-text-field
            :value="'Désactivé'"
            label="Champ désactivé"
            disabled
          />
        </div>

        <!-- RmrFileInput - wrapper around v-file-input with RMR styles -->
        <div class="ds-section-label mb-4">RmrFileInput</div>
        <div class="ds-form-grid mb-6">
          <rmr-file-input
            label="Fichier PDF"
            placeholder="Sélectionner un fichier"
            accept=".pdf"
            clearable
          />
          <rmr-file-input
            label="Fichier avec icône"
            placeholder="Sélectionner un fichier"
            accept=".pdf,.ppt,.pptx"
            clearable
            :icon="icons.mdiFilePdfBox"
          />
        </div>

        <!-- RmrSelect - wrapper around v-select, supports multiple + no-chips -->
        <div class="ds-section-label mb-4">RmrSelect</div>
        <div class="ds-form-grid mb-6">
          <rmr-select
            v-model="selectValues.single"
            :items="sampleItems"
            item-text="label"
            item-value="value"
            label="Select simple"
          />
          <rmr-select
            v-model="selectValues.multiple"
            :items="sampleItems"
            item-text="label"
            item-value="value"
            label="Select multiple no chips"
            no-chips
            multiple
          />
        </div>

        <!-- RmrAutocomplete - wrapper around v-autocomplete, supports multiple + no-chips -->
        <div class="ds-section-label mb-4">RmrAutocomplete</div>
        <div class="ds-form-grid mb-6">
          <rmr-autocomplete
            v-model="autocompleteValues.single"
            :items="sampleItems"
            item-text="label"
            item-value="value"
            label="Autocomplete simple"
          />
          <rmr-autocomplete
            v-model="autocompleteValues.multiple"
            :items="sampleItems"
            item-text="label"
            item-value="value"
            label="Autocomplete multiple no chips"
            no-chips
            multiple
          />
        </div>

        <div class="ds-section-label mb-4">RmrDatePickerField</div>
        <div class="ds-form-grid mb-6">
          <rmr-date-picker-field
            :value="monthPickerValue"
            :display-value="monthPickerLabel"
            type="month"
            close-on-input
            label="Mois"
            prepend-inner-icon="mdi-calendar-month"
            @input="updateMonthPickerValue"
          />
          <rmr-date-picker-field
            :value="rangePickerValue"
            :display-value="rangePickerLabel"
            range
            label="Période"
            prepend-inner-icon="mdi-calendar-range"
            :picker-props="{ max: todayStr }"
            @input="updateRangePickerValue"
          />
        </div>

        <!-- Checkbox - use class="rmr-checkbox" on v-checkbox for indigo color (SCSS class, no component) -->
        <div class="ds-section-label mb-4">Checkbox (.rmr-checkbox)</div>
        <div class="ds-control-row mb-6">
          <v-checkbox
            v-model="checkboxValues.empty"
            class="rmr-checkbox mt-0 pt-0"
            label="Unchecked"
          />
          <v-checkbox
            v-model="checkboxValues.checked"
            class="rmr-checkbox mt-0 pt-0"
            label="Checked"
          />
        </div>

        <!-- Slider - use class="rmr-slider" on v-slider for indigo track/thumb (SCSS class, no component) -->
        <div class="ds-section-label mb-4">Slider (.rmr-slider)</div>
        <div class="ds-slider-wrap">
          <v-slider
            v-model="sliderValue"
            class="rmr-slider"
            min="0"
            max="100"
            thumb-label="always"
          />
        </div>

        <!-- RmrToggleCard - clickable card that toggles a boolean, used for LLM selection -->
        <div class="ds-section-label mb-4">RmrToggleCard</div>
        <div class="ds-toggle-variants">
          <div class="ds-toggle-variants__row">
            <div class="ds-toggle-variants__label">Default</div>
            <div class="ds-toggle-grid">
              <rmr-toggle-card
                v-model="toggleValues.perplexity"
                label="Perplexity"
              />
              <rmr-toggle-card v-model="toggleValues.gemini" label="Gemini" />
              <rmr-toggle-card v-model="toggleValues.openai" label="ChatGPT" />
            </div>
          </div>

          <div class="ds-toggle-variants__row">
            <div class="ds-toggle-variants__label">Small</div>
            <div class="ds-toggle-grid">
              <rmr-toggle-card
                v-model="toggleValues.perplexitySm"
                size="sm"
                label="Perplexity"
              />
              <rmr-toggle-card
                v-model="toggleValues.geminiSm"
                size="sm"
                label="Gemini"
              />
              <rmr-toggle-card
                v-model="toggleValues.openaiSm"
                size="sm"
                label="ChatGPT"
              />
            </div>
          </div>
        </div>

        <div class="ds-section-label mt-8 mb-4">RmrSitePicker</div>
        <rmr-site-picker
          v-model="sitePickerValue"
          :sites="sitePickerSites"
          :search-keys="['name', 'domain', 'group']"
          search-placeholder="Rechercher un site, un domaine ou un groupe…"
          aria-label="Site de démonstration"
        >
          <template #metadata="{ site }">
            Groupe éditorial : {{ site.group }}
          </template>
        </rmr-site-picker>
      </v-card-text>
    </v-card>

    <!-- ================================================================ -->
    <!-- SECTION: RmrStatusChip                                           -->
    <!-- Props: label, variant, color, tooltip, tooltipStyle              -->
    <!-- MI statuses: Terminé, En cours, Erreur, À relancer, En attente   -->
    <!-- ================================================================ -->
    <v-card id="ds-feedback" class="ds-section mb-6">
      <v-card-title class="ds-section__head">
        <div class="ds-section__meta">
          <div class="ds-section__eyebrow">Feedback</div>
          <div class="ds-section__title">RmrStatusChip</div>
          <div class="ds-section__copy">
            Badges de statut sémantiques pour succès, progression, erreur et
            états neutres.
          </div>
        </div>
      </v-card-title>
      <v-card-text class="ds-chip-row">
        <rmr-status-chip label="Actif" variant="primary" />
        <rmr-status-chip label="Succès" variant="success" />
        <rmr-status-chip label="Rejeté" variant="error" />
        <rmr-status-chip label="En attente" variant="warning" />
        <rmr-status-chip
          label="Avec tooltip"
          variant="primary"
          tooltip="Avec tooltip — exemple de style"
          :tooltip-style="buildColoredTooltipStyle(colors.indigo)"
        />
      </v-card-text>
      <v-card-text class="ds-chip-row">
        <rmr-status-chip label="Terminé" variant="success" />
        <rmr-status-chip label="En cours" variant="info" />
        <rmr-status-chip label="Erreur" variant="error" />
        <rmr-status-chip label="À relancer" variant="warning" />
        <rmr-status-chip label="En attente" variant="neutral" />
        <rmr-status-chip label="Couleur libre" :color="colors.indigo" />
      </v-card-text>
    </v-card>

    <v-card class="ds-section mb-6">
      <v-card-title class="ds-section__head">
        <div class="ds-section__meta">
          <div class="ds-section__eyebrow">Feedback</div>
          <div class="ds-section__title">RmrNotice</div>
          <div class="ds-section__copy">
            Messages contextuels structurés pour informer ou avertir sans
            utiliser les alertes Vuetify par défaut.
          </div>
        </div>
      </v-card-title>
      <v-card-text>
        <rmr-notice title="Information" variant="info" class="mb-3">
          Une information utile qui accompagne l’action en cours.
        </rmr-notice>
        <rmr-notice title="Attention" variant="warning">
          Une contrainte à vérifier avant de continuer.
        </rmr-notice>
      </v-card-text>
    </v-card>

    <!-- ================================================================ -->
    <!-- SECTION: RmrSoftBadge                                            -->
    <!-- Pilule fond doux / texte coloré — à distinguer de RmrStatusChip, -->
    <!-- qui est un badge fond plein + texte blanc.                       -->
    <!-- Props: label, color, background, icon, iconSrc, title, size      -->
    <!-- ================================================================ -->
    <v-card id="ds-soft-badge" class="ds-section mb-6">
      <v-card-title class="ds-section__head">
        <div class="ds-section__meta">
          <div class="ds-section__eyebrow">Feedback</div>
          <div class="ds-section__title">RmrSoftBadge</div>
          <div class="ds-section__copy">
            Pilule discrète pour qualifier une ligne sans la surcharger :
            provenance, catégorie, type.
          </div>
        </div>
      </v-card-title>
      <v-card-text class="ds-chip-row">
        <rmr-soft-badge
          v-for="badge in softBadgeExamples"
          :key="badge.label"
          :label="badge.label"
          :color="badge.color"
          :background="badge.background"
        />
      </v-card-text>
      <v-card-text class="ds-chip-row">
        <rmr-soft-badge
          label="Avec icône"
          :color="colors.indigo"
          :background="colors.indigoBg"
          :icon="icons.mdiRssBox"
        />
        <rmr-soft-badge
          label="Taille xs"
          size="xs"
          :color="colors.indigo"
          :background="colors.indigoBg"
        />
        <rmr-soft-badge label="Neutre (valeurs par défaut)" />
      </v-card-text>
    </v-card>

    <!-- ================================================================ -->
    <!-- SECTION: RmrTabs                                                 -->
    <!-- Props: tabs (array of { key, label, icon }), value (v-model)     -->
    <!-- ================================================================ -->
    <v-card id="ds-layout" class="ds-section mb-6">
      <v-card-title class="ds-section__head">
        <div class="ds-section__meta">
          <div class="ds-section__eyebrow">Layout</div>
          <div class="ds-section__title">RmrTabs</div>
          <div class="ds-section__copy">
            Navigation secondaire compacte pour basculer entre vues d'un même
            contexte.
          </div>
        </div>
      </v-card-title>
      <v-card-text>
        <rmr-tabs v-model="activeTab" :tabs="tabs" class="mb-4" />
        <div class="ds-tabs-preview">
          Onglet actif : <strong>{{ tabs[activeTab].label }}</strong>
        </div>
      </v-card-text>
    </v-card>

    <!-- ================================================================ -->
    <!-- SECTION: RmrKeywordChips                                         -->
    <!-- Variants: default (light surfaces), ghost (dark/gradient)        -->
    <!-- Props: keywords, maxVisible, variant, label                      -->
    <!-- ================================================================ -->
    <v-card class="ds-section mb-6">
      <v-card-title class="ds-section__head">
        <div class="ds-section__meta">
          <div class="ds-section__eyebrow">Feedback</div>
          <div class="ds-section__title">RmrKeywordChips</div>
          <div class="ds-section__copy">
            Affichage compact de listes de mots-clés, avec gestion du
            dépassement et variante sombre.
          </div>
        </div>
      </v-card-title>
      <v-card-text>
        <div class="ds-section-label mb-4">Default</div>
        <rmr-keyword-chips
          :keywords="sampleKeywords"
          :max-visible="3"
          variant="default"
        />

        <div class="ds-section-label mb-4 mt-6">Ghost</div>
        <div class="ds-dark-bg">
          <rmr-keyword-chips
            :keywords="sampleKeywords"
            :max-visible="2"
            variant="ghost"
            label="Marché"
          />
        </div>
      </v-card-text>
    </v-card>

    <!-- ================================================================ -->
    <!-- SECTION: RmrLlmBadge                                             -->
    <!-- Props: provider ("perplexity"|"gemini"|"openai"), size, short    -->
    <!-- short=false: full name  |  short=true: short label + tooltip     -->
    <!-- ================================================================ -->
    <v-card class="ds-section mb-6">
      <v-card-title class="ds-section__head">
        <div class="ds-section__meta">
          <div class="ds-section__eyebrow">Feedback</div>
          <div class="ds-section__title">RmrLlmBadge</div>
          <div class="ds-section__copy">
            Badge fournisseur LLM, en version complète ou compacte avec tooltip.
          </div>
        </div>
      </v-card-title>
      <v-card-text>
        <div class="ds-badge-grid">
          <div
            v-for="provider in providers"
            :key="provider"
            class="ds-badge-item"
          >
            <div class="ds-item-label">{{ provider }}</div>
            <div class="ds-chip-row">
              <rmr-llm-badge :provider="provider" size="x-small" />
              <rmr-llm-badge :provider="provider" size="small" />
            </div>
          </div>
        </div>

        <div class="ds-section-label mb-4 mt-6">
          Short mode (compact label + tooltip)
        </div>
        <div class="d-flex align-center">
          <rmr-llm-badge provider="perplexity" short class="mr-1" />
          <rmr-llm-badge provider="gemini" short class="mr-1" />
          <rmr-llm-badge provider="openai" short />
        </div>
      </v-card-text>
    </v-card>

    <!-- ================================================================ -->
    <!-- SECTION: RmrActionMenu                                           -->
    <!-- Props: actions[{ key, label, icon, color, badgeBackground,       -->
    <!--        disabled }], triggerIcon, triggerVariant, triggerMode,    -->
    <!--        triggerLabel, triggerButtonVariant, triggerButtonColor,   -->
    <!--        triggerDisabled, size                                     -->
    <!-- Event: select(action)                                            -->
    <!-- ================================================================ -->
    <v-card class="ds-section mb-6">
      <v-card-title class="ds-section__head">
        <div class="ds-section__meta">
          <div class="ds-section__eyebrow">Actions</div>
          <div class="ds-section__title">RmrActionMenu</div>
          <div class="ds-section__copy">
            Menu d'actions compact à déclencheur simple, piloté par une liste
            d'actions déclarative.
          </div>
        </div>
      </v-card-title>
      <v-card-text>
        <div class="ds-section-label mb-4">Default</div>
        <div class="ds-chip-row">
          <rmr-action-menu :actions="actionMenuExamples" />
        </div>

        <div class="ds-section-label mb-4 mt-6">Button Trigger</div>
        <div class="ds-chip-row">
          <rmr-action-menu
            :actions="actionMenuExamples"
            trigger-mode="button"
            trigger-label="Options"
            :trigger-icon="icons.mdiFilePdfBox"
            trigger-button-variant="transparent"
            :trigger-button-color="colors.danger"
            size="xs"
          />
        </div>
      </v-card-text>
    </v-card>

    <!-- ================================================================ -->
    <!-- SECTION: RmrIconButton                                           -->
    <!-- Variants: edit (purple), delete (red), success (green), neutral  -->
    <!-- Props: icon, variant, size ("sm"|"xs"), disabled, loading,       -->
    <!--        tooltip                                                   -->
    <!-- ================================================================ -->
    <v-card class="ds-section mb-6">
      <v-card-title class="ds-section__head">
        <div class="ds-section__meta">
          <div class="ds-section__eyebrow">Actions</div>
          <div class="ds-section__title">RmrIconButton</div>
          <div class="ds-section__copy">
            Boutons d'icône secondaires pour édition, suppression, validation et
            actions neutres.
          </div>
        </div>
      </v-card-title>
      <v-card-text>
        <div class="ds-section-label mb-4">Variants</div>
        <div class="ds-chip-row mb-6">
          <rmr-icon-button
            :icon="icons.mdiPencil"
            variant="edit"
            tooltip="Modifier"
          />
          <rmr-icon-button
            :icon="icons.mdiTrashCan"
            variant="delete"
            tooltip="Supprimer"
          />
          <rmr-icon-button
            :icon="icons.mdiCheck"
            variant="success"
            tooltip="Valider"
          />
          <rmr-icon-button
            :icon="icons.mdiDotsVertical"
            variant="neutral"
            tooltip="Plus"
          />
        </div>

        <div class="ds-section-label mb-4">Sizes</div>
        <div class="ds-chip-row mb-6">
          <rmr-icon-button
            :icon="icons.mdiPencil"
            variant="edit"
            size="xs"
            tooltip="xs (26px)"
          />
          <rmr-icon-button
            :icon="icons.mdiPencil"
            variant="edit"
            size="sm"
            tooltip="sm (32px)"
          />
        </div>

        <div class="ds-section-label mb-4">States</div>
        <div class="ds-chip-row">
          <rmr-icon-button
            :icon="icons.mdiTrashCan"
            variant="delete"
            disabled
            tooltip="Désactivé"
          />
        </div>
      </v-card-text>
    </v-card>

    <!-- ================================================================ -->
    <!-- SECTION: RmrPageHero                                             -->
    <!-- Props: title, titleAccent, subtitle, icon, maxWidth              -->
    <!-- Slot #actions: buttons shown in the hero top-right area          -->
    <!-- ================================================================ -->
    <v-card class="ds-section mb-6">
      <v-card-title class="ds-section__head">
        <div class="ds-section__meta">
          <div class="ds-section__eyebrow">Layout</div>
          <div class="ds-section__title">RmrPageHero</div>
          <div class="ds-section__copy">
            Bloc d'en-tête éditorial pour les pages fortes, avec accent de titre
            et zone d'actions.
          </div>
        </div>
      </v-card-title>
      <v-card-text class="pa-0">
        <rmr-page-hero
          title="Design System"
          title-accent="RMR"
          subtitle="Composants partagés"
          :icon="icons.mdiPalette"
          max-width="100%"
        >
          <template #actions>
            <rmr-action-button
              label="Configurer"
              variant="translucent"
              :icon="icons.mdiCog"
            />
          </template>
        </rmr-page-hero>
      </v-card-text>
    </v-card>

    <!-- ================================================================ -->
    <!-- SECTION: Dialogues                                               -->
    <!-- RmrConfirmDialog: simple confirm/cancel modal (small, 440px)     -->
    <!-- RmrModal: full-size modal wrapper with gradient or form header   -->
    <!-- ================================================================ -->
    <v-card class="ds-section mb-6">
      <v-card-title class="ds-section__head">
        <div class="ds-section__meta">
          <div class="ds-section__eyebrow">Layout</div>
          <div class="ds-section__title">Dialogues</div>
          <div class="ds-section__copy">
            Confirmations et modales structurées pour les flux critiques et
            formulaires complexes.
          </div>
        </div>
      </v-card-title>
      <v-card-text>
        <div class="ds-dialog-trigger-grid mb-2">
          <div class="ds-dialog-trigger">
            <div class="ds-item-label">Confirmation Standard</div>
            <rmr-action-button
              label="Ouvrir dialog"
              variant="primary"
              @click="dialogs.confirm = true"
            />
          </div>
          <div class="ds-dialog-trigger">
            <div class="ds-item-label">Confirmation Suppression</div>
            <rmr-action-button
              label="Supprimer"
              variant="transparent"
              :color="colors.danger"
              :icon="icons.mdiDelete"
              @click="dialogs.delete = true"
            />
          </div>
          <div class="ds-dialog-trigger">
            <div class="ds-item-label">RmrModal — Variant Gradient</div>
            <rmr-action-button
              label="Ouvrir modal gradient"
              variant="primary"
              :icon="icons.mdiChartBox"
              @click="dialogs.modalGradient = true"
            />
          </div>
          <div class="ds-dialog-trigger">
            <div class="ds-item-label">RmrModal — Variant Form</div>
            <rmr-action-button
              label="Ouvrir modal form"
              variant="primary"
              :icon="icons.mdiAutoFix"
              @click="dialogs.modalForm = true"
            />
          </div>
        </div>

        <rmr-confirm-dialog
          v-model="dialogs.confirm"
          title="Confirmation"
          message="Êtes-vous sûr de vouloir continuer cette action ?"
          @confirm="dialogs.confirm = false"
          @cancel="dialogs.confirm = false"
        />
        <rmr-confirm-dialog
          v-model="dialogs.delete"
          title="Supprimer l'élément"
          message="Cette action est irréversible. L'élément sera définitivement supprimé."
          confirm-label="Supprimer"
          danger
          @confirm="dialogs.delete = false"
          @cancel="dialogs.delete = false"
        />

        <rmr-modal
          v-model="dialogs.modalGradient"
          variant="gradient"
          :icon="icons.mdiChartBox"
          title="Nom de l'analyse"
          eyebrow="Market Intelligence"
          max-width="600"
        >
          <template #header-subtitle>
            <div style="display: flex; gap: 6px; margin-top: 6px">
              <v-chip x-small dark color="rgba(255,255,255,0.18)">Paris</v-chip>
              <v-chip x-small dark color="rgba(255,255,255,0.18)">Lyon</v-chip>
            </div>
          </template>
          <template #header-actions>
            <v-chip small dark color="rgba(255,255,255,0.18)">En cours</v-chip>
          </template>
          <template #body>
            <v-card-text class="pa-6">
              <p style="color: #475569">
                Contenu du modal — onglets, timeline, ou tout autre corps.
              </p>
            </v-card-text>
          </template>
          <template #footer>
            <v-card-actions class="px-6 py-4">
              <v-spacer />
              <rmr-action-button
                label="Fermer"
                variant="ghost"
                @click="dialogs.modalGradient = false"
              />
              <rmr-action-button
                label="Modifier"
                variant="primary"
                :icon="icons.mdiPencil"
              />
            </v-card-actions>
          </template>
        </rmr-modal>
        <rmr-modal
          v-model="dialogs.modalForm"
          variant="form"
          :icon="icons.mdiAutoFix"
          title="Nouvelle analyse"
          subtitle="Configurez le marché, les LLMs et les questions à lancer."
          eyebrow="Market Intelligence"
          max-width="600"
          @close="dialogs.modalForm = false"
        >
          <template #body>
            <v-card-text class="pa-6">
              <p style="color: #475569">
                Contenu du modal — formulaire, stepper, ou tout autre corps.
              </p>
            </v-card-text>
          </template>
          <template #footer>
            <v-card-actions class="px-6 py-4">
              <v-spacer />
              <rmr-action-button
                label="Annuler"
                variant="ghost"
                @click="dialogs.modalForm = false"
              />
              <rmr-action-button label="Suivant" variant="primary" />
            </v-card-actions>
          </template>
        </rmr-modal>
      </v-card-text>
    </v-card>

    <!-- ================================================================ -->
    <!-- SECTION: RmrSlider                                               -->
    <!-- Props: value (Number, v-model) + all v-slider attrs via $attrs   -->
    <!-- Hardcodes: dense, hide-details, class="rmr-slider"              -->
    <!-- ================================================================ -->
    <v-card class="ds-section mb-6">
      <v-card-title class="ds-section__head">
        <div class="ds-section__meta">
          <div class="ds-section__eyebrow">Formulaires</div>
          <div class="ds-section__title">RmrSlider</div>
          <div class="ds-section__copy">
            Curseur numérique partagé, stylé pour les réglages rapides et les
            quantités.
          </div>
        </div>
      </v-card-title>
      <v-card-text>
        <rmr-slider
          v-model="sliderValue"
          label="Nombre de questions"
          min="1"
          max="100"
          thumb-label
        />
        <div class="mt-2 text-caption grey--text">
          {{ sliderValue }} question(s) par LLM
        </div>
      </v-card-text>
    </v-card>

    <!-- ================================================================ -->
    <!-- SECTION: RmrStepper                                              -->
    <!-- Props: value (Number, v-model), steps (Array of strings)        -->
    <!-- Slot: default → v-stepper-content items                         -->
    <!-- ================================================================ -->
    <v-card class="ds-section mb-6">
      <v-card-title class="ds-section__head">
        <div class="ds-section__meta">
          <div class="ds-section__eyebrow">Layout</div>
          <div class="ds-section__title">RmrStepper</div>
          <div class="ds-section__copy">
            Progression multi-étapes pour les parcours guidés et les formulaires
            complexes.
          </div>
        </div>
      </v-card-title>
      <v-card-text>
        <div class="ds-section-label mb-4">
          4 steps — steps 1 & 2 complete, step 3 active
        </div>
        <rmr-stepper
          v-model="stepperStep"
          :steps="['Identité', 'Ciblage', 'Configuration', 'Validation']"
        >
          <v-stepper-content step="1"
            ><div class="pa-4">Contenu étape 1</div></v-stepper-content
          >
          <v-stepper-content step="2"
            ><div class="pa-4">Contenu étape 2</div></v-stepper-content
          >
          <v-stepper-content step="3"
            ><div class="pa-4">Contenu étape 3 (actif)</div></v-stepper-content
          >
          <v-stepper-content step="4"
            ><div class="pa-4">Contenu étape 4</div></v-stepper-content
          >
        </rmr-stepper>
      </v-card-text>
    </v-card>

    <!-- ================================================================ -->
    <!-- SECTION: Color Tokens                                            -->
    <!-- All values defined in src/styles/_rmr-tokens.scss                -->
    <!-- JS equivalents (for :color props) in src/styles/tokens.js        -->
    <!-- ================================================================ -->
    <v-card id="ds-tokens" class="ds-section mb-6">
      <v-card-title class="ds-section__head">
        <div class="ds-section__meta">
          <div class="ds-section__eyebrow">Foundations</div>
          <div class="ds-section__title">Color Tokens</div>
          <div class="ds-section__copy">
            Palette de référence SCSS et JS utilisée pour les surfaces, accents,
            KPI et états sémantiques.
          </div>
        </div>
      </v-card-title>
      <v-card-text>
        <div v-for="group in colorGroups" :key="group.title" class="mb-6">
          <div class="ds-group-title">{{ group.title }}</div>
          <div class="ds-swatch-grid">
            <div
              v-for="swatch in group.items"
              :key="swatch.name"
              class="ds-swatch"
            >
              <div
                class="ds-swatch__box"
                :style="{ background: swatch.hex }"
              ></div>
              <div class="ds-swatch__name">{{ swatch.name }}</div>
              <div class="ds-swatch__hex">{{ swatch.hex }}</div>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- ================================================================ -->
    <!-- SECTION: Border Radius                                           -->
    <!-- All radius tokens defined in src/styles/_rmr-tokens.scss         -->
    <!-- ================================================================ -->
    <v-card class="ds-section mb-6">
      <v-card-title class="ds-section__head">
        <div class="ds-section__meta">
          <div class="ds-section__eyebrow">Foundations</div>
          <div class="ds-section__title">Border Radius</div>
          <div class="ds-section__copy">
            Échelle des rayons utilisée pour les cartes, composants compacts et
            formes pill.
          </div>
        </div>
      </v-card-title>
      <v-card-text>
        <div class="ds-radius-grid">
          <div v-for="item in radii" :key="item.name" class="ds-radius-item">
            <div class="ds-radius-preview">
              <div
                class="ds-radius-box"
                :style="{ borderRadius: item.size }"
              ></div>
            </div>
            <div class="ds-swatch__name">{{ item.name }}</div>
            <div class="ds-swatch__hex">{{ item.size }}</div>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- ================================================================ -->
    <!-- SECTION: Typography Scale                                        -->
    <!-- All font-size tokens defined in src/styles/_rmr-tokens.scss      -->
    <!-- ================================================================ -->
    <v-card class="ds-section mb-6">
      <v-card-title class="ds-section__head">
        <div class="ds-section__meta">
          <div class="ds-section__eyebrow">Foundations</div>
          <div class="ds-section__title">Typography Scale</div>
          <div class="ds-section__copy">
            Tailles typographiques partagées pour labels, corps, statistiques et
            KPI.
          </div>
        </div>
      </v-card-title>
      <v-card-text>
        <v-simple-table dense class="ds-typo-table">
          <thead>
            <tr>
              <th>Token</th>
              <th>Size</th>
              <th>Preview</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in typography" :key="item.name">
              <td>
                <code>{{ item.name }}</code>
              </td>
              <td>{{ item.size }}</td>
              <td :style="{ fontSize: item.size }">The quick brown fox</td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { computed, ref } from "@vue/composition-api";
import {
  mdiPalette,
  mdiCalendarMonth,
  mdiCalendarRange,
  mdiChartBox,
  mdiCog,
  mdiRocketLaunch,
  mdiPencil,
  mdiCheck,
  mdiTrashCan,
  mdiDelete,
  mdiDotsVertical,
  mdiAutoFix,
  mdiOpenInNew,
  mdiStar,
  mdiRobot,
  mdiAccount,
  mdiFilePdfBox,
  mdiLogoutVariant,
  mdiRssBox,
  mdiFormatListBulleted,
  mdiViewGridOutline,
} from "@mdi/js";
import DevMenu from "@/views/menus/DevMenu.vue";
import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import RmrPagination from "@/components/ui/actions/RmrPagination.vue";
import RmrSegmentedControl from "@/components/ui/actions/RmrSegmentedControl.vue";
import RmrNotice from "@/components/ui/feedback/RmrNotice.vue";
import RmrSoftBadge from "@/components/ui/feedback/RmrSoftBadge.vue";
import RmrDatePickerField from "@/components/ui/forms/RmrDatePickerField.vue";
import RmrFileInput from "@/components/ui/forms/RmrFileInput.vue";
import RmrTextField from "@/components/ui/forms/RmrTextField.vue";
import RmrSelect from "@/components/ui/forms/RmrSelect.vue";
import RmrSitePicker from "@/components/ui/forms/RmrSitePicker.vue";
import RmrAutocomplete from "@/components/ui/forms/RmrAutocomplete.vue";
import RmrStatusChip from "@/components/ui/feedback/RmrStatusChip.vue";
import RmrTabs from "@/components/ui/layout/RmrTabs.vue";
import RmrLlmBadge from "@/components/ui/feedback/RmrLlmBadge.vue";
import RmrKeywordChips from "@/components/ui/feedback/RmrKeywordChips.vue";
import RmrPageHero from "@/components/ui/layout/RmrPageHero.vue";
import RmrToggleCard from "@/components/ui/forms/RmrToggleCard.vue";
import RmrConfirmDialog from "@/components/ui/actions/RmrConfirmDialog.vue";
import RmrIconButton from "@/components/ui/actions/RmrIconButton.vue";
import RmrActionMenu from "@/components/ui/actions/RmrActionMenu.vue";
import RmrModal from "@/components/ui/layout/RmrModal.vue";
import RmrSlider from "@/components/ui/forms/RmrSlider.vue";
import RmrStepper from "@/components/ui/layout/RmrStepper.vue";
import { buildColoredTooltipStyle } from "@/helpers/build-colored-tooltip-style";
import {
  RMR_CA,
  RMR_CA_SOFT,
  RMR_DANGER,
  RMR_INDIGO,
  RMR_INDIGO_BG,
  RMR_ORANGE,
  RMR_RED_BG,
  RMR_RPM,
  RMR_RPM_SOFT,
  RMR_RPM_TINT,
  RMR_SESSIONS,
  RMR_SESSIONS_SOFT,
  RMR_SESSIONS_TINT,
} from "@/styles/tokens";

export default {
  name: "Chartes",
  components: {
    DevMenu,
    RmrActionButton,
    RmrPagination,
    RmrSegmentedControl,
    RmrNotice,
    RmrSoftBadge,
    RmrDatePickerField,
    RmrFileInput,
    RmrTextField,
    RmrSelect,
    RmrSitePicker,
    RmrAutocomplete,
    RmrStatusChip,
    RmrTabs,
    RmrLlmBadge,
    RmrKeywordChips,
    RmrPageHero,
    RmrToggleCard,
    RmrConfirmDialog,
    RmrIconButton,
    RmrActionMenu,
    RmrModal,
    RmrSlider,
    RmrStepper,
  },
  setup() {
    const icons = {
      mdiPalette,
      mdiCalendarMonth,
      mdiCalendarRange,
      mdiChartBox,
      mdiCog,
      mdiRocketLaunch,
      mdiPencil,
      mdiCheck,
      mdiTrashCan,
      mdiDelete,
      mdiDotsVertical,
      mdiAutoFix,
      mdiOpenInNew,
      mdiStar,
      mdiRobot,
      mdiAccount,
      mdiFilePdfBox,
      mdiLogoutVariant,
      mdiRssBox,
      mdiFormatListBulleted,
      mdiViewGridOutline,
    };

    const colors = {
      indigo: RMR_INDIGO,
      indigoBg: RMR_INDIGO_BG,
      danger: RMR_DANGER,
    };

    const segmentedValues = ref({
      sort: "date",
      view: "table",
      period: "24h",
      tab: "veille",
    });
    const segmentedSortItems = [
      { value: "date", label: "Date" },
      { value: "score", label: "Score" },
    ];
    const segmentedViewItems = [
      { value: "table", label: "Table", icon: mdiFormatListBulleted },
      { value: "cards", label: "Cartes", icon: mdiViewGridOutline },
    ];
    const segmentedPeriodItems = [
      { value: "24h", label: "24 h" },
      { value: "7j", label: "7 jours" },
      { value: "perso", label: "Perso." },
    ];
    const segmentedTabItems = [
      { value: "veille", label: "VEILLE GLOBALE" },
      { value: "urls", label: "LOT D'URLS" },
    ];

    const paginationPage = ref(3);

    const softBadgeExamples = [
      { label: "Sites concurrents", color: RMR_RPM, background: RMR_RPM_SOFT },
      { label: "RSS", color: RMR_ORANGE, background: RMR_CA_SOFT },
      { label: "Google News", color: RMR_INDIGO, background: RMR_INDIGO_BG },
      { label: "Marfeel", color: RMR_SESSIONS, background: RMR_SESSIONS_SOFT },
      { label: "Reddit", color: RMR_CA, background: RMR_CA_SOFT },
      { label: "Rappel Conso", color: RMR_DANGER, background: RMR_RED_BG },
    ];

    const activeTab = ref(0);
    const todayStr = new Date().toISOString().substring(0, 10);
    const textFieldValues = ref({
      empty: "",
      filled: "Valeur de démonstration",
    });
    const selectValues = ref({
      single: 1,
      multiple: [1, 3],
    });
    const autocompleteValues = ref({
      single: 2,
      multiple: [2, 4],
    });
    const sitePickerValue = ref(2);
    const sitePickerSites = [
      { id: 1, name: "Marmiton", domain: "marmiton.org", group: "Cuisine" },
      { id: 2, name: "Auto Plus", domain: "autoplus.fr", group: "Automobile" },
      { id: 3, name: "Biba", domain: "biba-magazine.fr", group: "Féminin" },
      { id: 4, name: "Grazia", domain: "grazia.fr", group: "Féminin" },
      {
        id: 5,
        name: "Maison & Travaux",
        domain: "maison-travaux.fr",
        group: "Maison",
      },
      { id: 6, name: "Top Santé", domain: "topsante.com", group: "Santé" },
      { id: 7, name: "Pleine Vie", domain: "pleinevie.fr", group: "Senior" },
    ];
    const monthPickerValue = ref("2026-03");
    const rangePickerValue = ref(["2026-03-01", "2026-03-12"]);
    const checkboxValues = ref({
      empty: false,
      checked: true,
    });
    const sliderValue = ref(68);
    const toggleValues = ref({
      perplexity: true,
      gemini: false,
      openai: false,
      perplexitySm: true,
      geminiSm: false,
      openaiSm: false,
    });
    const stepperStep = ref(3);

    const dialogs = ref({
      confirm: false,
      delete: false,
      modalGradient: false,
      modalForm: false,
    });

    const heroSections = [
      { id: "ds-actions", label: "Actions" },
      { id: "ds-forms", label: "Formulaires" },
      { id: "ds-feedback", label: "Feedback" },
      { id: "ds-layout", label: "Layout" },
      { id: "ds-tokens", label: "Tokens" },
    ];

    const sampleItems = [
      { label: "Alpha", value: 1 },
      { label: "Bravo", value: 2 },
      { label: "Charlie", value: 3 },
      { label: "Delta", value: 4 },
    ];
    const formatDateLabel = (dateStr, options) => {
      if (!dateStr) {
        return "";
      }

      return new Date(`${dateStr}T00:00:00`).toLocaleDateString(
        "fr-FR",
        options
      );
    };
    const monthPickerLabel = computed(() => {
      if (!monthPickerValue.value) {
        return "";
      }

      const [year, month] = monthPickerValue.value.split("-").map(Number);
      return new Date(year, month - 1, 1).toLocaleDateString("fr-FR", {
        month: "long",
        year: "numeric",
      });
    });
    const rangePickerLabel = computed(() => {
      if (
        !Array.isArray(rangePickerValue.value) ||
        rangePickerValue.value.length < 2
      ) {
        return "";
      }

      return `${formatDateLabel(rangePickerValue.value[0], {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })} — ${formatDateLabel(rangePickerValue.value[1], {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })}`;
    });
    const updateMonthPickerValue = (value) => {
      monthPickerValue.value = value;
    };
    const updateRangePickerValue = (value) => {
      rangePickerValue.value = value;
    };

    const tabs = [
      { key: "overview", label: "Vue d'ensemble", icon: mdiAccount },
      { key: "actions", label: "Actions", icon: mdiCog },
      { key: "ai", label: "IA", icon: mdiRobot },
      { key: "featured", label: "Favoris", icon: mdiStar },
    ];

    const providers = ["perplexity", "gemini", "openai"];
    const sampleKeywords = [
      "Paris",
      "Lyon",
      "Marseille",
      "Toulouse",
      "Bordeaux",
    ];

    const standardButtonExamples = [
      { label: "Primary", variant: "primary", icon: mdiRocketLaunch },
      { label: "Loading…", variant: "primary", loading: true, icon: mdiCog },
      { label: "Disabled", variant: "primary", disabled: true, icon: mdiCog },
      { label: "Transparent", variant: "transparent", icon: mdiPencil },
      {
        label: "Danger",
        variant: "transparent",
        color: RMR_DANGER,
        icon: mdiRocketLaunch,
      },
      {
        label: "Transparent disabled",
        variant: "transparent",
        color: RMR_DANGER,
        disabled: true,
        icon: mdiRocketLaunch,
      },
      { label: "Ghost", variant: "ghost", icon: mdiCog },
      {
        label: "Ghost disabled",
        variant: "ghost",
        disabled: true,
        icon: mdiCog,
      },
      { label: "Utility", variant: "utility", icon: mdiCog },
      {
        label: "Utility danger",
        variant: "utility",
        color: RMR_DANGER,
        icon: mdiLogoutVariant,
      },
    ];

    const buttonSizeExamples = [
      {
        label: "Primary xs",
        text: "XS",
        variant: "primary",
        size: "xs",
        icon: mdiPencil,
      },
      {
        label: "Primary sm",
        text: "SM",
        variant: "primary",
        size: "sm",
        icon: mdiPencil,
      },
      {
        label: "Primary md",
        text: "MD",
        variant: "primary",
        size: "md",
        icon: mdiPencil,
      },
      {
        label: "Primary lg",
        text: "LG",
        variant: "primary",
        size: "lg",
        icon: mdiPencil,
      },
      {
        label: "Ghost xs",
        text: "XS",
        variant: "ghost",
        size: "xs",
        icon: mdiCog,
      },
      {
        label: "Ghost sm",
        text: "SM",
        variant: "ghost",
        size: "sm",
        icon: mdiCog,
      },
      {
        label: "Ghost md",
        text: "MD",
        variant: "ghost",
        size: "md",
        icon: mdiCog,
      },
      {
        label: "Ghost lg",
        text: "LG",
        variant: "ghost",
        size: "lg",
        icon: mdiCog,
      },
      {
        label: "Utility xs",
        text: "XS",
        variant: "utility",
        size: "xs",
        icon: mdiCog,
      },
      {
        label: "Utility sm",
        text: "SM",
        variant: "utility",
        size: "sm",
        icon: mdiCog,
      },
      {
        label: "Utility md",
        text: "MD",
        variant: "utility",
        size: "md",
        icon: mdiCog,
      },
      {
        label: "Utility lg",
        text: "LG",
        variant: "utility",
        size: "lg",
        icon: mdiCog,
      },
    ];

    const translucentButtonExamples = [
      { label: "Translucent", variant: "translucent", icon: mdiCog },
      { label: "Active", variant: "translucent", active: true, icon: mdiCog },
      {
        label: "Translucent disabled",
        variant: "translucent",
        disabled: true,
        icon: mdiCog,
      },
    ];

    const actionMenuExamples = [
      {
        key: "view",
        label: "Voir l'analyse",
        icon: mdiOpenInNew,
        color: RMR_SESSIONS,
        badgeBackground: RMR_SESSIONS_TINT,
      },
      {
        key: "edit",
        label: "Modifier",
        icon: mdiPencil,
        color: RMR_INDIGO,
        badgeBackground: RMR_RPM_TINT,
      },
      {
        key: "delete",
        label: "Supprimer",
        icon: mdiTrashCan,
        color: RMR_DANGER,
        badgeBackground: RMR_RED_BG,
        disabled: true,
      },
    ];

    const colorGroups = [
      {
        title: "Brand",
        items: [
          { name: "$rmr-indigo", hex: "#6366F1" },
          { name: "$rmr-indigo-dark", hex: "#312E81" },
          { name: "$rmr-indigo-accent", hex: "#4338CA" },
          { name: "$rmr-indigo-bg", hex: "#EEF2FF" },
          { name: "$rmr-indigo-light", hex: "#E0E7FF" },
          { name: "$rmr-indigo-border", hex: "#C7D2FE" },
          { name: "$rmr-rpm", hex: "#7C3AED" },
        ],
      },
      {
        title: "KPI",
        items: [
          { name: "$rmr-ca", hex: "#EA580C" },
          { name: "$rmr-ca-light", hex: "#F97316" },
          { name: "$rmr-ca-soft", hex: "#FFF7ED" },
          { name: "$rmr-ca-tint", hex: "#FED7AA" },
          { name: "$rmr-sessions", hex: "#0284C7" },
          { name: "$rmr-sessions-dark", hex: "#0369A1" },
          { name: "$rmr-sessions-soft", hex: "#F0F9FF" },
          { name: "$rmr-sessions-tint", hex: "#BAE6FD" },
          { name: "$rmr-rpm", hex: "#7C3AED" },
          { name: "$rmr-rpm-dark", hex: "#6D28D9" },
          { name: "$rmr-rpm-soft", hex: "#F5F3FF" },
          { name: "$rmr-rpm-tint", hex: "#DDD6FE" },
          { name: "$rmr-green", hex: "#059669" },
          { name: "$rmr-green-bg", hex: "#ECFDF5" },
        ],
      },
      {
        title: "Text",
        items: [
          { name: "$rmr-text-primary", hex: "#0F172A" },
          { name: "$rmr-text-secondary", hex: "#475569" },
          { name: "$rmr-text-subtle", hex: "#94A3B8" },
        ],
      },
      {
        title: "Surface",
        items: [
          { name: "$rmr-border", hex: "#F1F5F9" },
          { name: "$rmr-border-hover", hex: "#E2E8F0" },
          { name: "$rmr-surface-1", hex: "#FAFBFC" },
          { name: "$rmr-surface-2", hex: "#F8FAFC" },
          { name: "$rmr-surface-0", hex: "#FFFFFF" },
        ],
      },
      {
        title: "Semantic",
        items: [
          { name: "$rmr-success", hex: "#56CA00" },
          { name: "$rmr-success-bg", hex: "#E8F5EA" },
          { name: "$rmr-danger", hex: "#DC2626" },
          { name: "$rmr-danger-bg", hex: "#FEE2E2" },
          { name: "$rmr-info", hex: "#1E88E5" },
          { name: "$rmr-red", hex: "#DC2626" },
          { name: "$rmr-red-bg", hex: "#FEE2E2" },
          { name: "$rmr-orange", hex: "#D97706" },
        ],
      },
      {
        title: "Runtime / JS",
        items: [
          { name: "RMR_SUCCESS", hex: "#56CA00" },
          { name: "RMR_INFO", hex: "#1E88E5" },
          { name: "RMR_ORANGE", hex: "#D97706" },
          { name: "RMR_CA_SOFT", hex: "#FFF7ED" },
          { name: "RMR_CA_TINT", hex: "#FED7AA" },
          { name: "RMR_SESSIONS_SOFT", hex: "#F0F9FF" },
          { name: "RMR_SESSIONS_TINT", hex: "#BAE6FD" },
          { name: "RMR_RPM_SOFT", hex: "#F5F3FF" },
          { name: "RMR_RPM_TINT", hex: "#DDD6FE" },
          { name: "RMR_ON_DARK_MUTED", hex: "rgba(255,255,255,0.5)" },
          { name: "RMR_LLM_PERPLEXITY", hex: "#7C3AED" },
          { name: "RMR_LLM_GEMINI", hex: "#1E88E5" },
          { name: "RMR_LLM_OPENAI", hex: "#10B981" },
        ],
      },
    ];

    const radii = [
      { name: "$rmr-radius-xs", size: "4px" },
      { name: "$rmr-radius-sm", size: "8px" },
      { name: "$rmr-radius-md", size: "10px" },
      { name: "$rmr-radius-lg", size: "12px" },
      { name: "$rmr-radius-xl", size: "14px" },
      { name: "$rmr-radius-2xl", size: "16px" },
      { name: "$rmr-radius-3xl", size: "18px" },
      { name: "$rmr-radius-4xl", size: "20px" },
      { name: "$rmr-radius-pill", size: "999px" },
    ];

    const typography = [
      { name: "$rmr-fs-xs", size: "9px" },
      { name: "$rmr-fs-sm", size: "10px" },
      { name: "$rmr-fs-base", size: "11px" },
      { name: "$rmr-fs-md", size: "12px" },
      { name: "$rmr-fs-body", size: "13px" },
      { name: "$rmr-fs-title", size: "14px" },
      { name: "$rmr-fs-stat", size: "16px" },
      { name: "$rmr-fs-proj", size: "20px" },
      { name: "$rmr-fs-kpi", size: "38px" },
    ];

    const jumpToSection = (id) => {
      const section = document.getElementById(id);
      if (!section) {
        return;
      }

      section.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    };

    return {
      icons,
      colors,
      buildColoredTooltipStyle,
      heroSections,
      activeTab,
      todayStr,
      stepperStep,
      textFieldValues,
      selectValues,
      autocompleteValues,
      sitePickerSites,
      sitePickerValue,
      monthPickerValue,
      monthPickerLabel,
      rangePickerValue,
      rangePickerLabel,
      updateMonthPickerValue,
      updateRangePickerValue,
      checkboxValues,
      sliderValue,
      toggleValues,
      dialogs,
      sampleItems,
      tabs,
      providers,
      sampleKeywords,
      standardButtonExamples,
      buttonSizeExamples,
      translucentButtonExamples,
      segmentedValues,
      segmentedSortItems,
      segmentedViewItems,
      segmentedPeriodItems,
      segmentedTabItems,
      paginationPage,
      softBadgeExamples,
      actionMenuExamples,
      colorGroups,
      radii,
      typography,
      jumpToSection,
    };
  },
};
</script>

<style lang="scss" scoped>
.design-system {
  padding-bottom: 40px;
}

.ds-hero {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  align-items: stretch;
  padding: 28px 32px;
  border-radius: $rmr-radius-4xl;
  border: 1px solid $rmr-indigo-border;
  background: radial-gradient(
      circle at top right,
      rgba($rmr-indigo, 0.12),
      transparent 28%
    ),
    linear-gradient(135deg, $rmr-surface-0 0%, $rmr-indigo-bg 100%);
  box-shadow: $rmr-shadow-card;
}

.ds-hero__content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 760px;
}

.ds-hero__eyebrow {
  font-size: $rmr-fs-sm;
  font-weight: 800;
  letter-spacing: 1.1px;
  text-transform: uppercase;
  color: $rmr-indigo;
}

.ds-hero__title {
  margin: 0;
  font-size: 42px;
  line-height: 1.05;
  letter-spacing: -1.4px;
  color: $rmr-text-primary;
}

.ds-hero__copy {
  margin: 0;
  max-width: 640px;
  font-size: $rmr-fs-title;
  line-height: 1.7;
  color: $rmr-text-secondary;
}

.ds-hero__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.ds-hero__tag {
  appearance: none;
  padding: 8px 12px;
  border-radius: $rmr-radius-pill;
  border: 1px solid $rmr-indigo-border;
  background: rgba($rmr-surface-0, 0.84);
  font-size: $rmr-fs-base;
  font-weight: 700;
  color: $rmr-indigo-dark;
}

.ds-hero__tag--link {
  cursor: pointer;
  transition: transform 0.16s ease, border-color 0.16s ease,
    box-shadow 0.16s ease;
}

.ds-hero__tag--link:hover {
  transform: translateY(-1px);
  border-color: rgba($rmr-indigo, 0.28);
  box-shadow: 0 8px 18px rgba($rmr-indigo, 0.08);
}

.ds-hero__side {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-end;
  gap: 16px;
  min-width: 160px;
}

.ds-section {
  overflow: hidden;
  scroll-margin-top: 24px;
  border-radius: $rmr-radius-2xl !important;
  border: 1px solid $rmr-border !important;
  background: linear-gradient(180deg, $rmr-surface-0 0%, $rmr-surface-1 100%);
  box-shadow: $rmr-shadow-card !important;
}

.ds-section__head {
  padding: 22px 24px 18px !important;
  border-bottom: 1px solid $rmr-border;
  background: linear-gradient(
    180deg,
    rgba($rmr-indigo, 0.03) 0%,
    rgba($rmr-surface-0, 0) 100%
  );
}

.ds-section__meta {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ds-section__eyebrow {
  font-size: $rmr-fs-sm;
  font-weight: 800;
  color: $rmr-indigo;
  letter-spacing: 0.9px;
  text-transform: uppercase;
}

.ds-section__title {
  font-size: 22px;
  font-weight: 800;
  line-height: 1.1;
  color: $rmr-text-primary;
}

.ds-section__copy {
  max-width: 760px;
  font-size: $rmr-fs-body;
  line-height: 1.65;
  color: $rmr-text-secondary;
}

.ds-section ::v-deep .v-card__text {
  padding: 24px !important;
}

.ds-button-grid,
.ds-badge-grid,
.ds-segmented-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
}

.ds-button-item,
.ds-badge-item,
.ds-segmented-item,
.ds-segmented-block {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  border-radius: $rmr-radius-xl;
  border: 1px solid $rmr-border;
  background: rgba($rmr-surface-0, 0.9);
}

.ds-segmented-item {
  align-items: flex-start;
}

.ds-section-label,
.ds-item-label,
.ds-group-title {
  font-size: 12px;
  font-weight: 700;
  color: $rmr-text-secondary;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.ds-item-label--on-dark {
  color: rgba(255, 255, 255, 0.8);
}

.ds-form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 20px;
}

.ds-control-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px 24px;
  align-items: center;
}

.ds-slider-wrap {
  max-width: 420px;
  margin-bottom: 24px;
  padding: 18px 18px 8px;
  border-radius: $rmr-radius-xl;
  border: 1px solid $rmr-border;
  background: rgba($rmr-surface-0, 0.92);
}

.ds-toggle-variants {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.ds-toggle-variants__row {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.ds-toggle-variants__label {
  width: 72px;
  padding-top: 10px;
  color: $rmr-text-muted;
  font-size: $rmr-fs-base;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  flex: 0 0 auto;
}

.ds-toggle-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
  flex: 1 1 auto;
}

.ds-chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}

.ds-dialog-trigger-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
}

.ds-dialog-trigger {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border-radius: $rmr-radius-xl;
  border: 1px solid $rmr-border;
  background: rgba($rmr-surface-0, 0.92);
}

.ds-dark-bg {
  background: linear-gradient(135deg, $rmr-indigo-dark 0%, $rmr-indigo 100%);
  border-radius: $rmr-radius-lg;
  padding: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-start;
  border: 1px solid rgba($rmr-indigo-dark, 0.08);
}

.ds-dark-bg__item {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ds-tabs-preview {
  font-size: 14px;
  color: $rmr-text-secondary;
}

.ds-swatch-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(132px, 1fr));
  gap: 16px;
}

.ds-radius-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(148px, 1fr));
  gap: 16px;
}

.ds-swatch {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px;
  border-radius: $rmr-radius-xl;
  border: 1px solid $rmr-border;
  background: rgba($rmr-surface-0, 0.92);
}

.ds-radius-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px;
  border-radius: $rmr-radius-xl;
  border: 1px solid $rmr-border;
  background: rgba($rmr-surface-0, 0.92);
}

.ds-swatch__box {
  width: 72px;
  height: 72px;
  border-radius: 12px;
  border: 1px solid $rmr-border-hover;
}

.ds-radius-preview {
  width: 76px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ds-radius-box {
  width: 72px;
  height: 46px;
  background: linear-gradient(135deg, $rmr-indigo 0%, $rmr-rpm 100%);
  border: 1px solid $rmr-indigo-border;
}

.ds-swatch__name {
  font-size: 12px;
  font-weight: 700;
  color: $rmr-text-primary;
}

.ds-swatch__hex,
.ds-typo__size {
  font-size: 12px;
  color: $rmr-text-muted;
}

.ds-typo-table {
  ::v-deep th {
    font-size: 12px !important;
    font-weight: 700 !important;
    color: $rmr-text-secondary !important;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }

  ::v-deep td {
    color: $rmr-text-primary;
  }
}

.rmr-slider {
  ::v-deep .v-slider__track-fill,
  ::v-deep .v-slider__thumb {
    background-color: $rmr-indigo !important;
    border-color: $rmr-indigo !important;
  }

  ::v-deep .v-slider__thumb-label {
    background-color: $rmr-indigo !important;
  }
}

.rmr-checkbox {
  ::v-deep .v-icon {
    color: $rmr-indigo !important;
  }
}

@media (max-width: 960px) {
  .ds-hero {
    flex-direction: column;
    padding: 24px;
  }

  .ds-hero__title {
    font-size: 34px;
  }

  .ds-hero__side {
    align-items: flex-start;
  }

  .ds-hero__stat {
    align-items: flex-start;
  }
}
</style>
